import React from 'react';
import { logError } from '@utils/usefulFuncs';
import { useCompanyResolution } from './useCompanyResolution';
import { useFeeds } from './useFeeds';
import { useFeedViewUrlState } from './useFeedViewUrlState';

// The query being composed, and the feed list it is composed against.
//
// src/contexts/ holds seventeen providers and src/features/feedView held none, against this repo's
// own rule that feature-local state prefers Context over prop threading. The cost showed in two
// places:
//
//   <FeedViewForm onFormChange={setFormState} … />   // the raw useState setter, across a boundary
//
// The form never used it as one. Its very first line narrows it back down —
// `setField(patch) => onFormChange(prev => ({ ...prev, ...patch }))` — so the narrowing existed
// already and simply lived on the wrong side of the boundary, where a caller could still do anything
// it liked with the setter. `patchForm` is that same narrowing, owned by the thing that owns the
// state.
//
// And the feed list: moving the feed picker into the page header meant re-threading `feeds`,
// `isLoadingFeeds`, `feedsError` and `onRetryFeeds` out of FeedViewForm, through the page, and into
// wherever it landed. They are facts about the query surface rather than about the form's layout, so
// they belong here and a picker can be rendered anywhere.
//
// WHAT IS DELIBERATELY NOT HERE: the result, the column negotiation and the drill-down path. Those
// describe the READ, not the query, and each already has a hook that owns it. Folding them in would
// make this the page in a different file.

// Placeholders that log rather than fail silently, so a component rendered outside the Provider says
// so in the console instead of quietly doing nothing when the analyst types.
const defaultState = {
  formState: null,
  isFormCollapsed: false,
  feeds: [],
  isLoadingFeeds: false,
  feedsError: null,
  patchForm: () => logError('FeedViewContext: patchForm called outside a FeedViewProvider'),
  resetForm: () => logError('FeedViewContext: resetForm called outside a FeedViewProvider'),
  collapseForm: () => logError('FeedViewContext: collapseForm called outside a FeedViewProvider'),
  expandForm: () => logError('FeedViewContext: expandForm called outside a FeedViewProvider'),
  reloadFeeds: () => logError('FeedViewContext: reloadFeeds called outside a FeedViewProvider'),
  companyResolution: {
    text: '',
    isResolving: false,
    progress: { done: 0, total: 0 },
    result: null,
    hasPendingText: false,
    setText: () => logError('FeedViewContext: setText called outside a FeedViewProvider'),
    dismiss: () => logError('FeedViewContext: dismiss called outside a FeedViewProvider'),
    flush: () => logError('FeedViewContext: flush called outside a FeedViewProvider'),
  },
};

export const FeedViewContext = React.createContext(defaultState);

const ACTIONS = {
  PATCH: 'PATCH',
  RESET: 'RESET',
  COLLAPSE: 'COLLAPSE',
  EXPAND: 'EXPAND',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.PATCH:
      return { ...state, formState: { ...state.formState, ...action.patch } };
    // Back to exactly the first-load query. The initial state is a constant for this reason.
    case ACTIONS.RESET:
      return { ...state, formState: action.initial, isFormCollapsed: false };
    // Folded once a result lands, so the table starts near the top rather than below the whole form.
    case ACTIONS.COLLAPSE:
      return state.isFormCollapsed ? state : { ...state, isFormCollapsed: true };
    // Expanding is a deliberate act and sticks until the next result arrives.
    case ACTIONS.EXPAND:
      return state.isFormCollapsed ? { ...state, isFormCollapsed: false } : state;
    default:
      return state;
  }
};

export const FeedViewProvider = ({ initialFormState, children }) => {
  const [state, dispatch] = React.useReducer(reducer, {
    formState: initialFormState,
    isFormCollapsed: false,
  });

  const { feeds, isLoading: isLoadingFeeds, error: feedsError, reload: reloadFeeds } = useFeeds();

  const patchForm = React.useCallback((patch) => dispatch({ type: ACTIONS.PATCH, patch }), []);

  // Here rather than in the form, and that is what deletes the `onSubmit(override)` back-channel.
  // Submitting has to resolve whatever text is still in the box, and the resolved list has to reach
  // the request in the SAME TICK — a patch dispatched now is not readable by the handler that runs
  // next. The form used to solve that by reaching forward into its parent's next-tick state; with
  // the query and the pending text under one owner, the submit handler can simply await the flush.
  const companyResolution = useCompanyResolution({
    companies: state.formState?.companies,
    onCompaniesChange: React.useCallback((next) => patchForm({ companies: next }), [patchForm]),
  });

  // Consumed in ONE place, which is the point of it living here: no component reads `location`, and
  // a picker moved into the page header inherits a shareable query without knowing one exists.
  useFeedViewUrlState(state.formState, patchForm);

  const resetForm = React.useCallback(
    () => dispatch({ type: ACTIONS.RESET, initial: initialFormState }),
    [initialFormState]
  );
  const collapseForm = React.useCallback(() => dispatch({ type: ACTIONS.COLLAPSE }), []);
  const expandForm = React.useCallback(() => dispatch({ type: ACTIONS.EXPAND }), []);

  // Individual properties in the dependency list rather than `state`, so a form keystroke does not
  // re-render every consumer that only reads the feed list.
  const value = React.useMemo(
    () => ({
      formState: state.formState,
      isFormCollapsed: state.isFormCollapsed,
      feeds,
      isLoadingFeeds,
      feedsError,
      patchForm,
      resetForm,
      collapseForm,
      expandForm,
      reloadFeeds,
      companyResolution,
    }),
    [
      state.formState,
      state.isFormCollapsed,
      feeds,
      isLoadingFeeds,
      feedsError,
      patchForm,
      resetForm,
      collapseForm,
      expandForm,
      reloadFeeds,
      companyResolution,
    ]
  );

  return <FeedViewContext.Provider value={value}>{children}</FeedViewContext.Provider>;
};
